import React, { memo, useMemo } from 'react';
import { PlatformShopAuthWayType } from './components/PlatformShopAuthWay';
import { useTranslate } from '@tolgee/react';
import { BottomButton, Footer, Label, LittleTips } from './components/styles';
import CheckboxGroup from './components/CheckboxGroup';
import { useSystemLanguageValue } from '../../hooks/business/system/useSystem';
import useOpenURL from './hooks/useOpenURL';
import { RightOutlined } from '@ant-design/icons';
import { Button, Image } from 'antd';
import SMEN from './assets/sm_en.png';
import SMCN from './assets/sm_cn.png';
import PreviewVideo from '../PreviewVideo';
import ThumbEN from './assets/thumb_en.png';
import ThumbCN from './assets/thumb_cn.png';

export const serviceMarketURL: any = {
  SG: 'https://marketplace.lazada.sg/web/subscribe/list.html',
  TH: 'https://marketplace.lazada.co.th/web/subscribe/list.html',
  MY: 'https://marketplace.lazada.com.my/web/subscribe/list.html',
  PH: 'https://marketplace.lazada.com.ph/web/subscribe/list.html',
  VN: 'https://marketplace.lazada.vn/web/subscribe/list.html',
  ID: 'https://marketplace.lazada.co.id/web/subscribe/list.html',
};

interface LazadaStepB2Props {
  authWay: PlatformShopAuthWayType;
  onChange?: (value: PlatformShopAuthWayType) => void;
  region: General.UpperCaseRegion;
  onOpenURL?: () => void;
  onCopyURL?: (guideUrl?: string) => void;
}
export const LazadaStepB2 = memo((props: LazadaStepB2Props) => {
  const { t } = useTranslate();
  const { onChange, onOpenURL, onCopyURL, region, authWay } = props;
  const lang = useSystemLanguageValue();
  const openURL = useOpenURL();
  const authWayOptions: Array<{ value: PlatformShopAuthWayType; render: React.ReactNode }> =
    useMemo(
      () => [
        {
          value: PlatformShopAuthWayType.Official,
          render: t('authShop.authWayOption7'),
        },
        {
          value: PlatformShopAuthWayType.External,
          render: t('authShop.authWayOption8'),
        },
      ],
      [t],
    );
  return (
    <div>
      <Label>{t('authShop.您可选择以下任意方式添加店铺')}</Label>
      <CheckboxGroup
        layout="vertical"
        value={authWay}
        options={authWayOptions}
        onChange={(val) => onChange && onChange(val as PlatformShopAuthWayType)}
      />
      <div className="border border-solid border-[#f0f2f5] bg-[#f8f9fa] min-h-[150px] max-h-[27vh] flex overflow-hidden flex-col">
        <div className="flex-grow h-[32px] flex-shrink-0 flex-grow-0 flex border-0 border-b border-solid border-[#f0f2f5] justify-between items-center text-[12px] text-[#232426] px-[20px]">
          <div>{t('操作步骤')}</div>
          <div
            className="cursor-pointer"
            onClick={() => {
              const openMode = 'external';
              openURL(
                lang === 'zh-CN'
                  ? 'https://leyan.yuque.com/czpqnb/nmiwyb/cuev843dv7pptegb'
                  : 'https://chatplus.ganjutech.com/wiki/en/#authorize-lazada-store',
                openMode,
              );
            }}
          >
            {t('查看详细指引')}
          </div>
        </div>
        <div className="flex-grow overflow-auto px-[20px] py-[18px] text-[12px] text-[#515359] leading-[18px]">
          <div className="px-auto [&_img]:w-[203px] [&_svg]:!w-[26px] [&_svg]:!h-[26px] mb-[12px]">
            <PreviewVideo
              mask
              openExternal
              url={
                lang === 'zh-CN'
                  ? 'https://clients.zhisuitech.com/fett-public/lazada_auth_guide_cn.mp4'
                  : 'https://clients.zhisuitech.com/fett-public/lazada_auth_guide_en.mp4'
              }
              thumbUrl={lang === 'zh-CN' ? ThumbCN : ThumbEN}
              className="!w-[205px] mx-auto"
            />
          </div>
          <div>
            {t('1. 登录服务市场，订购ChatPlusAI（选择【仅授权】、【半年】）；')}{' '}
            <span
              onClick={() => onOpenURL?.()}
              className="text-[var(--ant-primary-color)] cursor-pointer"
            >
              {t('enterprise.前往订购')}
            </span>
          </div>
          <div>
            {t(
              '2. 完成订购后，点击授权服务-选择ChatPlusAI（或通过Lazad服务市场首页-我的个人中心-我的服务-选择ChatPlusAI），点击【使用服务】（可参考下方图片）并同意数据传输；',
            )}
            <span
              onClick={async () => {
                const openMode = 'external';
                openURL(serviceMarketURL[region], openMode);
              }}
              className="text-[var(--ant-primary-color)] cursor-pointer"
            >
              {t('前往我的服务')}
            </span>
          </div>
          <div>{t('3. ChatPlusAI请求打开页面，选择店铺所属客服部门，确认添加后完成授权。')}</div>
          <div className="px-[50px] h-[80px] overflow-hidden [&_.ant-image-mask]:items-start [&_.ant-image-mask-info]:pt-[34px]">
            <Image src={lang === 'zh-CN' ? SMCN : SMEN} preview />
          </div>
        </div>
      </div>
      <Footer className="flex-col items-center mt-[30px]">
        <LittleTips
          type="info"
          message={
            authWay === PlatformShopAuthWayType.Official
              ? t(
                  '小提示：点击后会直接打开服务市场页面，请确保默认浏览器为您日常登录店铺后台所使用的浏览器；若不是，请复制链接，前往日常登录店铺后台的浏览器中进行粘贴操作。若已登录非授权店铺，需要先退出，重新登录需要授权的店铺账号',
                )
              : t('小提示：复制链接后，请在日常登录店铺后台的浏览器中进行粘贴操作。')
          }
          showIcon
        />
        {authWay === PlatformShopAuthWayType.Official ? (
          <>
            <BottomButton size="large" type="primary" onClick={() => onOpenURL?.()}>
              {t('前往Lazada服务市场')}
              <RightOutlined />
            </BottomButton>
            <Button onClick={() => onCopyURL?.()} type="link" className="mt-[10px]">
              {t('authShop.复制链接')}
            </Button>
          </>
        ) : (
          <>
            <BottomButton
              size="large"
              type="primary"
              onClick={() =>
                onCopyURL?.(
                  lang === 'zh-CN'
                    ? 'https://leyan.yuque.com/czpqnb/nmiwyb/cuev843dv7pptegb'
                    : 'https://chatplus.ganjutech.com/wiki/en/#authorize-lazada-store',
                )
              }
            >
              {t('authShop.复制链接')}
              <RightOutlined />
            </BottomButton>
          </>
        )}
      </Footer>
    </div>
  );
});

LazadaStepB2.displayName = 'LazadaStepB2';
